/**
 * FinanzasCuentasPage.jsx — Gestión de Cuentas del Módulo de Finanzas para CIG
 * Ruta: /finanzas/cuentas
 */
import { useState, useEffect, useCallback } from 'react';
import finanzasApi from '../../lib/finanzas_api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Wallet, Pencil, Trash2, Search, Building2 } from 'lucide-react';
import FinAppLayout from '../../components/FinAppLayout';

const CURRENCIES = ['CLP', 'USD', 'ARS'];
const currencySymbols = { CLP: '$', USD: 'US$', ARS: 'AR$' };

export default function FinanzasCuentasPage() {
  const [cuentas, setCuentas] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntidad, setSelectedEntidad] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({ nombre: '', moneda_base: 'CLP', moneda_equivalente: 'none', entidad_id: 'none' });
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [cRes, eRes] = await Promise.all([finanzasApi.get('/cuentas'), finanzasApi.get('/entidades')]);
      setCuentas(cRes.data); setEntidades(eRes.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => { setForm({ nombre: '', moneda_base: 'CLP', moneda_equivalente: 'none', entidad_id: 'none' }); setEditingId(null); };

  const openCreate = () => {
    resetForm();
    if (selectedEntidad !== 'all') setForm(f => ({ ...f, entidad_id: selectedEntidad }));
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setForm({ nombre: c.nombre, moneda_base: c.moneda_base, moneda_equivalente: c.moneda_equivalente || 'none', entidad_id: c.entidad_id });
    setEditingId(c.id); setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error('El nombre es requerido'); return; }
    if (!form.entidad_id || form.entidad_id === 'none') { toast.error('Selecciona una entidad'); return; }
    setSaving(true);
    try {
      const payload = { ...form, moneda_equivalente: form.moneda_equivalente === 'none' ? null : form.moneda_equivalente };
      if (editingId) {
        await finanzasApi.put(`/cuentas/${editingId}`, { nombre: payload.nombre, moneda_equivalente: payload.moneda_equivalente });
        toast.success('Cuenta actualizada');
      } else {
        await finanzasApi.post('/cuentas', payload); toast.success('Cuenta creada');
      }
      setDialogOpen(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const confirmDelete = (c) => { setDeleteTarget(c); setDeleteDialogOpen(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await finanzasApi.delete(`/cuentas/${deleteTarget.id}`);
      toast.success('Cuenta eliminada'); setDeleteDialogOpen(false); setDeleteTarget(null); fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const getEntidadName = (id) => entidades.find(e => e.id === id)?.nombre || '—';
  const filtered = cuentas.filter(c => {
    const matchSearch = c.nombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEntidad = selectedEntidad === 'all' || c.entidad_id === selectedEntidad;
    return matchSearch && matchEntidad;
  });
  const availableEquivCurrencies = CURRENCIES.filter(c => c !== form.moneda_base);

  return (
    <FinAppLayout>
      <div data-testid="finanzas-cuentas-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-800 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Finanzas · Cuentas
            </h1>
            <p className="mt-1 text-base text-slate-500">Gestiona tus cuentas corrientes multidivisa</p>
          </div>
          <Button onClick={openCreate} disabled={entidades.length === 0} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl shadow-sm gap-2 h-10" data-testid="create-cuenta-btn">
            <Plus className="w-4 h-4" strokeWidth={2} /> Nueva Cuenta
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Buscar cuenta..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-10 bg-white border-slate-200 rounded-xl" data-testid="search-cuentas-input" />
          </div>
          <Select value={selectedEntidad} onValueChange={setSelectedEntidad}>
            <SelectTrigger className="w-[200px] h-10 bg-white border-slate-200 rounded-xl" data-testid="filter-entidad-select"><SelectValue placeholder="Filtrar por entidad" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las entidades</SelectItem>
              {entidades.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {entidades.length === 0 && !loading ? (
          <div className="text-center py-20" data-testid="no-entidades-hint">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-amber-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-semibold text-slate-700">Primero crea una entidad</h3>
            <p className="mt-2 text-sm text-slate-500">Necesitas al menos una entidad para crear cuentas</p>
          </div>
        ) : loading ? (
          <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-cuentas">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
              <Wallet className="w-8 h-8 text-indigo-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-semibold text-slate-700">{searchTerm ? 'Sin resultados' : 'Sin cuentas'}</h3>
            <p className="mt-2 text-sm text-slate-500">{searchTerm ? 'Intenta con otro término' : 'Crea tu primera cuenta corriente'}</p>
          </div>
        ) : (
          <Card className="border border-slate-100 rounded-xl shadow-sm overflow-hidden" data-testid="cuentas-table-card">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                  {['Nombre', 'Entidad', 'Moneda Base', 'Moneda Equiv.', 'Acciones'].map((h, i) => (
                    <TableHead key={h} className={`font-semibold text-xs text-slate-500 uppercase tracking-wider ${i === 4 ? 'text-right' : ''}`}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((cuenta) => (
                  <TableRow key={cuenta.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`cuenta-row-${cuenta.id}`}>
                    <TableCell className="font-medium text-slate-700">{cuenta.nombre}</TableCell>
                    <TableCell className="text-slate-500">{getEntidadName(cuenta.entidad_id)}</TableCell>
                    <TableCell><Badge className="bg-indigo-50 text-indigo-700 border border-indigo-100">{currencySymbols[cuenta.moneda_base]} {cuenta.moneda_base}</Badge></TableCell>
                    <TableCell>
                      {cuenta.moneda_equivalente
                        ? <Badge className="bg-slate-50 text-slate-600 border border-slate-200">{currencySymbols[cuenta.moneda_equivalente]} {cuenta.moneda_equivalente}</Badge>
                        : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(cuenta)} className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" data-testid={`edit-cuenta-${cuenta.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => confirmDelete(cuenta)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-cuenta-${cuenta.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md rounded-xl" data-testid="cuenta-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingId ? 'Editar Cuenta' : 'Nueva Cuenta'}</DialogTitle>
              <DialogDescription>{editingId ? 'Modifica los datos' : 'Crea una cuenta corriente'}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label>Entidad</Label>
                <Select value={form.entidad_id} onValueChange={(v) => setForm({ ...form, entidad_id: v })} disabled={!!editingId}>
                  <SelectTrigger className="h-10 bg-slate-50/50" data-testid="cuenta-entidad-select"><SelectValue placeholder="Seleccionar entidad" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Seleccionar entidad</SelectItem>
                    {entidades.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Cuenta Principal" required className="h-10 bg-slate-50/50" data-testid="cuenta-nombre-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Moneda Base</Label>
                  <Select value={form.moneda_base} onValueChange={(v) => setForm({ ...form, moneda_base: v })} disabled={!!editingId}>
                    <SelectTrigger className="h-10 bg-slate-50/50" data-testid="cuenta-moneda-base-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{currencySymbols[c]} {c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Moneda Equivalente</Label>
                  <Select value={form.moneda_equivalente || 'none'} onValueChange={(v) => setForm({ ...form, moneda_equivalente: v === 'none' ? '' : v })} disabled={!!editingId}>
                    <SelectTrigger className="h-10 bg-slate-50/50" data-testid="cuenta-moneda-equiv-select"><SelectValue placeholder="Ninguna" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Ninguna</SelectItem>
                      {availableEquivCurrencies.map(c => <SelectItem key={c} value={c}>{currencySymbols[c]} {c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl" data-testid="cuenta-save-btn">
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-sm rounded-xl" data-testid="delete-cuenta-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-600">Eliminar Cuenta</DialogTitle>
              <DialogDescription>¿Eliminar <strong>{deleteTarget?.nombre}</strong>? Se eliminarán todos los movimientos asociados.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-xl">Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete} className="rounded-xl" data-testid="delete-cuenta-confirm-btn">Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </FinAppLayout>
  );
}
